/*
 * ============================================================================
 * SAINTE NOIRE — SITE COPY
 * ============================================================================
 * This is the ONE file to edit if you want to change wording on the site.
 * Save and refresh — no build step, no restart. The site picks it up instantly.
 *
 * Rules of thumb:
 *   1. Keep the quotes. Every string is wrapped in "double quotes".
 *   2. Don't remove commas at the end of lines.
 *   3. Italics use <span className="italic">word</span>. You can add or remove.
 *   4. If you want a line break inside a headline, use <br /> where you want the break.
 *   5. To hide the internal discovery form entirely, set discovery.enabled = false below.
 *
 * If you break something, git-revert this file — none of the other code depends on any specific string.
 * ============================================================================
 */

export const COPY = {
  brand: {
    name: "Sainte Noire",
    audit_label: "Brand Evaluation",
    audit_tagline: "© Sainte Noire beyond branding, world building",
    footer_left: "Sainte Noire — Beyond branding. World building.",
    footer_right: "© Sainte Noire",
  },

  landing: {
    eyebrow: "Free Brand Evaluation",
    headline: (
      <>
        Your business has evolved.
        <br />
        <span className="italic font-normal">Has your brand kept up?</span>
      </>
    ),
    subhead:
      "First things first: this isn't a personality quiz. It's a complimentary Brand Evaluation designed to help you understand what your brand is communicating today, and whether that's helping or hurting where you're wanting to go. After years of building brands, websites, launches, and campaigns, we've noticed most brands fall into one of seven patterns. None of them are permanent. Most are simply the result of a business growing faster than the brand currently surrounding it. Answer eleven carefully chosen questions, add your website, and we'll send you a personalized Brand Evaluation explaining what we're noticing, why it matters, and where we'd start if your brand landed on our desk.",
    meta: "11 Questions · 5 Minutes · Complimentary",
    cta: "Start My Evaluation",
    value_props_label: "Why this exists",
    value_props: [
      {
        n: "01",
        title: "Built for clarity.",
        body: "This isn't here to give you a score. It's here to help you understand what your brand may be communicating that you can't always see from the inside.",
      },
      {
        n: "02",
        title: "Perception is everything.",
        body: "Founders experience the business from the inside. Customers don't. Sometimes those two perspectives line up beautifully. Sometimes they don't. When you have to choose, how your business is viewed by the world is what matters more.",
      },
      {
        n: "03",
        title: "We look for patterns.",
        body: "Every evaluation is written from the patterns inside your answers. The goal isn't to tell you everything that's \"wrong.\" It's to help you understand what we see, why we see it, and what matters most for your brand, right now.",
      },
    ],
  },

  assessment: {
    counter_format: "{current}/{total}", // used as-is; do not change the {current}/{total} tokens
    back_label: "Back",
    exit_label: "Exit",
    next_label: "Next",
    finish_label: "Finish",
    loading_label: "Loading Questions…",
    load_error: "Failed to load questions.",
  },

  cinematic: {
    heading_label: "Analyzing — please hold",
    hold_note: "This may take up to 30 seconds",
    phases: [
      "Scanning your responses…",
      "Fetching your website…",
      "Analyzing typography & tone…",
      "Evaluating brand voice…",
      "Diagnosing category gaps…",
      "Drafting the editor's note…",
    ],
  },

  lead_form: {
    step_label: "Step — Final",
    eyebrow: "Almost there",
    headline: (
      <>
        We've found <span className="italic">the gap.</span>
      </>
    ),
    subhead:
      "Tell us a little about the brand behind the answers and we'll turn your results into a personalized diagnosis.",
    fields: {
      first_name: { label: "First Name", placeholder: "Jane" },
      email: { label: "Email Address", placeholder: "jane@company.com" },
      business_name: {
        label: "Business / Brand Name",
        placeholder: "Sainte Noire",
      },
      website: {
        label: "Website URL",
        optional_note: "— optional",
        placeholder: "https://yourbrand.com",
        helper:
          "Add your website and we'll use it alongside your answers to make your analysis more specific to your actual brand.",
      },
    },
    submit_label: "Show Me My Results",
    submit_hint: "Instant · Emailed to you",
  },

  results: {
    header_left: "Sainte Noire — Free Brand Evaluation",
    header_right_prefix: "Prepared for",
    hero_eyebrow: "Your Brand Evaluation",
    sections: {
      diagnosis: {
        label: "What We're Noticing",
        title_prefix: "About Your",
      },
      cost: {
        label: "What It's Costing You",
        title: "Where This Shows Up",
      },
      symptoms: {
        label: "Where We See It",
        title: "The Patterns",
      },
      fix_first: {
        label: "Where We'd Start",
        title: "If Your Brand Landed On Our Desk Today",
      },
      secondary: {
        label: "Also Worth Noticing",
      },
    },
    prescription_block: {
      eyebrow: "Where To From Here",
      headline: (
        <>
          This is the work
          <br />
          <span className="italic">we were made for.</span>
        </>
      ),
      the_prescription_label: "Your Best Fit",
      best_for_prefix: "",
    },
    email_confirm_sent: "Evaluation Emailed",
    email_confirm_pending: "Email Pending",
    email_body_sent: "A copy has been sent to",
    email_body_pending: "We couldn't reach your inbox — please save this page.",
    restart_label: "Take It Again",
  },

  /**
   * CTA & Discovery
   * ---------------------------------------------------------------
   * "external_url" — button opens the url below in a new tab (Dubsado / Calendly / anything).
   * "inline_form"  — reveals the built-in discovery form (saves to admin dashboard).
   * "both"         — shows the external button PLUS a smaller "or send a project brief" link
   *                  that reveals the internal form. Recommended.
   */
  cta: {
    mode: "external_url", // "external_url" | "inline_form" | "both"
    external: {
      label: "Start Your Project",
      url: "https://www.saintenoire.co/work-with-us",
      open_in_new_tab: true,
    },
    inline_form_toggle_label: "Or send us a project brief",
  },

  discovery: {
    enabled: false,           // set to false to remove the secondary form option entirely
    // "dubsado_embed" — show your Dubsado form as an iframe embed.
    // "internal"      — show the built-in form that saves to the admin dashboard.
    // "off"           — hide the secondary form option (only the Book-A-Call button shows).
    mode: "dubsado_embed",   // "dubsado_embed" | "internal" | "off"
    dubsado_embed: {
      // Paste ONLY the src url from your Dubsado embed code:
      src: "https://portal.saintenoire.co/public/form/view/6a6148f376af1032f47eeda6?iframe=true",
      initial_height: 750,   // starting height in px; auto-resizes after load
    },
    // Small link label shown above the embedded form (leave empty to hide).
    embed_reveal_label: "Or send us a project brief",
    intro_label: "A few last questions before we connect",
    fields: {
      project_brief: {
        label: "Project Brief",
        hint: "Tell us what you're building and what's not working yet.",
        placeholder:
          "A few sentences about the business, the moment you're in, and what you're hoping to solve.",
      },
      role: { label: "Your Role", placeholder: "Founder, CEO, Head of Brand…" },
      instagram: { label: "Instagram", placeholder: "@yourbrand" },
      how_found: {
        label: "How Did You Find Us?",
        placeholder: "Referral, Instagram, a specific project…",
      },
      budget: {
        label_prefix: "Investment Range for", // becomes "Investment Range for {Offer Name}"
        hint: "This helps us understand fit before we get on a call.",
      },
      timeline: { label: "Preferred Timeline" },
    },
    submit_label: "Request A Discovery Call",
    submit_sending_label: "Sending…",
    success_eyebrow: "Request Received",
    success_headline: "We'll be in touch shortly.",
    success_body:
      "A member of the Sainte Noire team will review your brief and reach out within two business days.",
    toast_success: "Request received. We'll be in touch shortly.",
    toast_error: "Something went wrong. Please try again.",
    // Budget tier options — keyed by prescription offer name.
    // You can freely rename the labels; keep the ids unique.
    budget_options: {
      "Brand DNA": [
        { id: "under_10k", label: "Under $10k" },
        { id: "10_25k", label: "$10k – $25k" },
        { id: "25_50k", label: "$25k – $50k" },
        { id: "flex", label: "Flexible for the right partner" },
      ],
      "The Brand Campaign": [
        { id: "25_50k", label: "$25k – $50k" },
        { id: "50_100k", label: "$50k – $100k" },
        { id: "100_250k", label: "$100k – $250k" },
        { id: "250k+", label: "$250k+" },
        { id: "flex", label: "Flexible for the right partner" },
      ],
      "The FROW": [
        { id: "under_5k_mo", label: "Under $5k / month" },
        { id: "5_10k_mo", label: "$5k – $10k / month" },
        { id: "10_20k_mo", label: "$10k – $20k / month" },
        { id: "20k_mo+", label: "$20k+ / month" },
        { id: "flex", label: "Flexible for the right partner" },
      ],
    },
    timeline_options: [
      { id: "asap", label: "ASAP — within 30 days" },
      { id: "1_3", label: "1 – 3 months" },
      { id: "3_6", label: "3 – 6 months" },
      { id: "exploring", label: "Just exploring" },
    ],
  },
};
